import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  Container,
  Grid,
  Button,
  FormControl,
  Typography,
  TextField,
} from "@mui/material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem
} from "@mui/material";
import {
  List,
  ListSubheader,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Box
} from "@mui/material";
import { PictureAsPdf as PictureAsPdfIcon, BrokenImage as BrokenImageIcon } from "@mui/icons-material";
import dayjs from 'dayjs';
import SaveIcon from '@mui/icons-material/Save';
import CompleteIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, useNavigate } from "react-router-dom";
import { usePrompt } from "../../../../../../common/usePrompt"
import Confirmation from "../../../../../../common/Comfirmation";
import { GetStudy, UpdateReport, CompleteStudy, GetToken, GetPriorities } from "../../../../../../api";
import RichText from "../../../../../../common/EditorMCE";
import { PRIORITIES } from "../../../../../../constants/Consult";
import ReportingData from "./ReportingData";
import PdfPreview from "../../../shared/PdfPreview"
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { getFilename } from '../../../shared/fileDrop';

const SkeletonList = props =>
  <Box sx={{ width: 300 }}>
    <Skeleton />
    <Skeleton />
    <Skeleton />
  </Box>
let changeTimeout
const defaultFields = [
  { label: "Diagnostic interpretation" },
  { label: "Conclusions" },
  { label: "Comments & Recommendations" },
]

const isEditorAlive = editor => editor && !editor.destroyed && editor.getBody()
const isEditorEmpty = editor => {
  if (!isEditorAlive(editor)) return true
  return editor.getContent({ format: 'text' }).trim() === '' && !editor.getBody().querySelector('img');
}

const StudyRespond = ({ access }) => {
  const params = useParams();
  const [content, setContent] = useState([])
  const [study, setStudy] = useState(false);
  const [studies, setStudies] = useState();
  const [open, setOpen] = useState(false);
  const [newField, setNewField] = useState("");
  const [billing, setBilling] = useState([]);
  const [priority, setPriority] = useState();
  const [customPriorities, setCustomPriorities] = useState(null)
  const navigate = useNavigate();
  const [pdfPreviewDialogState, setPdfPreviewDialogState] = useState({
    open: false,
    studyId: null,
  });
  const [changes, setChanges] = useState(true)
  const [autoSave, setAutoSave] = useState()
  const autoSaveRef = React.useRef(false)
  const [confirm, setConfirm] = useState({
    open: false,
    text: "",
    callback: undefined
  })

  const didRun = React.useRef(false); // Prevent double render in dev mode

  const confirmOk = () => {
    confirm.callback && confirm.callback()
    confirmCancel()
  }
  const confirmCancel = () => {
    setConfirm(
      (i) => (i = { open: false, text: "", callback: undefined })
    )
  }

  const updateReport = useCallback(() => {
    if (!study || !content) return Promise.resolve()
    const report = content.filter(e=>e.ref?.current).map(e=>({label: e.label, html: (!isEditorEmpty(e.ref.current) && e.ref.current.getContent()) || null}))
    const isRefPriority = customPriorities && typeof priority === 'string' && priority.length === 24
    return UpdateReport(study.id, { report, billing: billing.map(e => e.id), priority: isRefPriority ? null : priority, priorityRef: isRefPriority ? priority : null, autoSave: autoSaveRef.current }).then(() => setChanges(false)).catch(e => e)
  }, [study, content, billing, priority, customPriorities])

  const goBack = useCallback(num => {
    if (window.history.state && window.history.state.idx > 0) navigate(num)
    else navigate('/', { replace: true })
  }, [navigate])

  React.useEffect(() => {
    if (!didRun.current && params.studyId !== study?.id) {
      didRun.current = true
      GetStudy(params.studyId).then((resp) => {
        if (resp.org?.billing?.length)
          resp.org.billing.push({ name: 'No Charge', id: '64a83ee84024d7967168ef9b' })

        // Next line of code cannot run twice so didRun is required for this section
        // If it runs twice the editor references will be overwritten
        setContent(() => {
          if (Array.isArray(resp.report) && resp.report.length) return resp.report
          else if (Array.isArray(resp.prelimReport) && resp.prelimReport.length) return resp.prelimReport
          else if (Array.isArray(resp.org?.settings?.reportFields) && resp.org.settings.reportFields.length) return resp.org.settings.reportFields.map(label=>({label}))
          return defaultFields
        })

        setBilling(resp.billing)
        setPriority(resp.priorityRef?.id || resp.priorityRef || resp.priority)
        const reportingOrgId = resp.reOrg?.id || resp.org?.id
        if (reportingOrgId) GetPriorities(reportingOrgId).then(cp => setCustomPriorities(cp?.length ? cp : null)).catch(() => setCustomPriorities(null))

        const org = resp.associateOrg?.id || resp.sentBy?.org?.id || access.org?.id
        const { PatientID, PatientName } = resp.PatientMainDicomTags;
        document.title = PatientName
        if (PatientID && org)
          GetStudy(null, { view: 'patient', org, PatientID }).then(setStudies).catch(e => e)

        setStudy(resp)
      }).catch(e => e)
    }
    return () => {
      if (changeTimeout) {
        clearTimeout(changeTimeout)
        changeTimeout = undefined
      }
    }
  }, [params.studyId, study, access])

  useEffect(() => {
    const title = document.title;
    return () => document.title = title
  }, [])

  useEffect(() => {
    if (autoSave) {
      autoSaveRef.current = true
      updateReport().then(() => {
        autoSaveRef.current = false
        console.log('Auto saved')
      })
      setAutoSave()
    }
  }, [updateReport, autoSave])

  usePrompt("There are unsaved changes, are you sure you want to leave the page?", changes)

  if (!study) return <React.Fragment />;

  const setEditorRef = (index, ref) => {
    setContent(content => {
      content[index].ref = ref
      return content
    })
  }

  const editorChange = () => {
    if (!changeTimeout)
      changeTimeout = setTimeout(() => {
        setAutoSave(true)
        changeTimeout = undefined
      }, 15000)
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    updateReport().then(() => goBack(-1));
  }

  const handlePdfPreviewDialog = () => {
    updateReport().then(() =>
      setPdfPreviewDialogState({ open: true, studyId: `${study.id}` })
    )
  };

  const handleSaveDialog = () => {
    if (newField.length) {
      setContent((e) => [...e, { label: newField }])
      setOpen(false)
    }
  }

  const completeStudy = (id) => {
    updateReport().then(() =>
      CompleteStudy(id).then(() => {
        setContent()
        setStudy()
        goBack(-1)
      }).catch(e => e)
    )
  }

  const medDream = StudyInstanceUID => {
    GetToken({ data: StudyInstanceUID }).then(result => {
      let { accessToken } = result
      accessToken && window.open(`https://dream.pacs.corridor.vet/?token=${accessToken}`, '_blank')
    }).catch(e => e)
    return false
  }

  const deleteField = (label, index) =>
    setConfirm(
      (i) =>
        (i = {
          open: true,
          text: `Are you sure you want to delete: ${label}?`,
          callback: () => {
            setContent(e => {
              const c = [...e]
              c.splice(index, 1)
              return c
            })
          }
        })
      )

  return (
    <Container>
      {priority === null || priority === undefined ? null : (
        <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
          <TextField
            id="priority"
            value={priority}
            label="Priority"
            onChange={e => setPriority(e.target.value)}
            variant="standard"
            select
            required
          >
            {customPriorities ? (
              customPriorities.filter(p => study.mobile ? p.mobile : !p.mobile).sort((a, b) => a.sortOrder - b.sortOrder).map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))
            ) : (
              PRIORITIES.map((e) => (e.id !== '' && !e.hidden &&
                <MenuItem key={e.id} value={e.id}>
                  {e.name}
                </MenuItem>
              ))
            )}
          </TextField>
        </FormControl>
      )}
      {!!study.org?.billing?.length && (
        <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
          <Autocomplete
            id="billing"
            freeSolo={false}
            multiple
            name="billing"
            options={(study.reOrg?.billing || study.org?.billing || [])}
            filterSelectedOptions
            getOptionLabel={o => o.name}
            onChange={(e, v) => setBilling(v)}
            value={billing}
            isOptionEqualToValue={(o, v) => !study.org?.settings?.duplicateBillingCodes && o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} required variant="standard" label="Billing Codes" />
            )}
          />
        </FormControl>
      )}
      <ReportingData study={study} section="consult" studyId={study.id} />
      {(studies &&
        <List
          sx={{ bgcolor: 'transparent' }}
          aria-labelledby="nested-list-subheader"
          subheader={
            <ListItemButton color="secondary" dense={false} component="a" sx={{ px: 0 }} onClick={() => studies && medDream(studies.filter(e => e.Series?.length).map(e => e.MainDicomTags.StudyInstanceUID).join(','))}>
              <ListSubheader disableSticky={true} sx={{ bgcolor: 'transparent', fontSize: 'inherit' }} component="div" id="nested-list-subheader" color="primary">
                Studies (view all)
              </ListSubheader>
            </ListItemButton>
          }
        >
          {studies.map(study => {
            const date = new Date(study.MainDicomTags.StudyTimestamp)
            const outputOptions = {
              year: "2-digit",
              month: "numeric",
              day: "numeric",
              hour: study.MainDicomTags.StudyTime ? "numeric" : undefined,
              minute: study.MainDicomTags.StudyTime ? "numeric" : undefined,
            }
            const reportOutputOptions = {
              year: "2-digit",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
              timeZone: study.org?.timezone || 'America/Los_Angeles'
            }
            const reportDate = study.completeAt && dayjs(study.completeAt.last || study.completeAt.first).toDate()
            return (
              <>
                <ListItem disablePadding key={study.id}>
                  <ListItemButton dense={false} disabled={!study.Series?.length} component="a" onClick={() => medDream(study.MainDicomTags.StudyInstanceUID)}>
                    <ListItemIcon><BrokenImageIcon /></ListItemIcon>
                    <ListItemText primary={date.toLocaleString("en-US", outputOptions)} />
                  </ListItemButton>
                </ListItem>
                {reportDate && (
                  <ListItem sx={{ ml: 2 }} disablePadding>
                    <ListItemButton dense={false} component="a" onClick={() => setPdfPreviewDialogState({ open: true, studyId: `${study.id}` })}>
                      <ListItemIcon><PictureAsPdfIcon /></ListItemIcon>
                      <ListItemText primary={reportDate.toLocaleString("en-US", reportOutputOptions)} />
                    </ListItemButton>
                  </ListItem>
                )}
              </>
            )
          })}
        </List>
      ) ||
        <SkeletonList />
      }
      {!!study.media?.length && (
        <List>
          <ListSubheader disableSticky={true} sx={{ bgcolor: 'transparent', fontSize: 16 }} component="div" id="nested-list-subheader">
            Multimedia
          </ListSubheader>
          {study.media.map((file, index) =>
            <ListItem disablePadding key={file}>
              <ListItemButton component="a" href={file}>
                <ListItemIcon>
                  <FilePresentIcon />
                </ListItemIcon>
                <ListItemText sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }} primary={getFilename(file)} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      )}
      <form onSubmit={handleSubmit}>
        <Grid
          sx={{ mb: 3, mt: 3 }}
          container
          spacing={4}
          justifyContent="left"
        >
          {content?.map((item, index) => (
            <FormControl key={item.label || index} fullWidth variant="standard">
              <Grid container direction="row">
                <Grid size={11}>
                  <Typography component="h2" variant="h6" color={(item.label && item.label[0] === '*' && 'secondary') || 'default'}>
                    {(item.label ? (item.label[0] === '*' && item.label.slice(1)) || item.label : '')}:
                </Typography>
                </Grid>
                <Grid size={1}>
                  <Button color="inherit" onClick={()=> deleteField(item.label, index)}>
                    <DeleteIcon />
                  </Button>
                </Grid>
              </Grid>
              <RichText
                onEditorRef={ref => setEditorRef(index, ref)}
                initialValue={item.html || '<p></p>'}
                onEditorChange={editorChange}
              />
            </FormControl>
          ))}
        </Grid>
        <Grid container sx={{ mb: 3 }} spacing={3} justifyContent="center">
          <FormControl fullWidth variant="standard">
            <Button color="secondary" onClick={()=> setOpen(true)} startIcon={<TextFieldsIcon />}>
              Add Field
            </Button>
          </FormControl>
          <Dialog open={open} maxWidth="sm" fullWidth onClose={() => setOpen(false)}>
            <DialogTitle>Add Field</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Add a rich text field to the report for this study
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="desc"
                label="Field Name"
                type="desc"
                fullWidth
                variant="standard"
                onChange={(e) => setNewField(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleSaveDialog}>Save</Button>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>
        </Grid>
        <Grid container spacing={3} justifyContent="left">
          <Button variant="outlined" color="primary" type="submit" startIcon={<SaveIcon />}>
            Save
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={study.org?.billing?.length && !billing.length}
            onClick={() => completeStudy(study.id)}
            startIcon={<CompleteIcon />}
            sx={{ "&:disabled": { backgroundColor: 'rgba(206, 147, 216, 0.25)' } }}
          >
            Complete
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={handlePdfPreviewDialog}
            startIcon={<PictureAsPdfIcon />}
          >
            Pdf Preview
          </Button>
        </Grid>
      </form>
      <PdfPreview
        open={pdfPreviewDialogState.open}
        studyId={pdfPreviewDialogState.studyId}
        setDialogState={setPdfPreviewDialogState}
      />
      <Confirmation
        open={confirm.open}
        onCancel={confirmCancel}
        onConfirm={confirmOk}
      >
        {confirm.text}
      </Confirmation>
    </Container>
  );
};

export default StudyRespond;
